import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, Mail, MessageCircle, Sparkles } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';
import { Textarea } from '@/components/ui/textarea';
import { PillTabs, RelativeTime, SectionCard } from '../../../../components/design-system/admin';
import { DeskState, FollowUpChannel, FollowUpDraft, LeadFile, useLeadDeskApi } from '../../../../common/hooks/use-lead-desk-api.hook';
import { useEmailApi } from '../../../../common/hooks/use-email-api.hook';
import { emailHtmlFor, whatsappLinkFor } from '../lead-desk-view';

type Lang = 'auto' | 'fr' | 'en' | 'es' | 'ar';
const LANGS: Lang[] = ['auto', 'fr', 'en', 'es', 'ar'];

interface Draft {
    text: string;
    subject: string;
    api: FollowUpDraft | null;
}

/**
 * The follow-up, from draft to sent: Ardi writes it in the shopper's language
 * with their render, the salesperson reads and edits it, then opens WhatsApp
 * on the shopper's number or sends it from the shop's own mail server — and
 * the desk records that it went.
 */
export function FollowUpComposer({ lead, onSent }: { lead: LeadFile; onSent: (desk: DeskState) => void }) {
    const { t } = useTranslation();
    const api = useLeadDeskApi();
    const emailApi = useEmailApi();
    const unsubscribed = !!lead.consent.unsubscribedAt;
    const [channel, setChannel] = useState<FollowUpChannel>(
        lead.contact.preferred === 'email' || !lead.contact.phone ? 'email' : 'whatsapp',
    );
    const [lang, setLang] = useState<Lang>('auto');
    const [drafts, setDrafts] = useState<Record<FollowUpChannel, Draft>>({
        whatsapp: { text: '', subject: '', api: null },
        email: { text: '', subject: '', api: null },
    });
    const [writing, setWriting] = useState(false);
    const [sending, setSending] = useState(false);
    const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
    const draft = drafts[channel];

    const update = (patch: Partial<Draft>) => setDrafts((all) => ({ ...all, [channel]: { ...all[channel], ...patch } }));

    const write = async () => {
        setWriting(true);
        setNotice(null);
        try {
            const result = await api.draftFollowUp(lead.id, { channel, lang: lang === 'auto' ? undefined : lang });
            update({ text: result.text, subject: result.subject ?? '', api: result });
            if (lang === 'auto') setLang(result.lang);
        } catch {
            setNotice({ tone: 'error', text: t('LEAD_DESK.COMPOSER.DRAFT_FAILED') });
        } finally {
            setWriting(false);
        }
    };

    const markSent = async (via: 'whatsapp' | 'email') => {
        const desk = await api.markSent(lead.id, { channel: via, text: draft.text });
        onSent(desk);
        setNotice({ tone: 'ok', text: via === 'email' ? t('LEAD_DESK.COMPOSER.SENT') : t('LEAD_DESK.COMPOSER.MARKED') });
    };

    const sendEmail = async () => {
        setSending(true);
        setNotice(null);
        try {
            const result = await emailApi.sendEmail({
                email: lead.email,
                subject: draft.subject || draft.api?.subject || '',
                htmlPayload: emailHtmlFor(draft.text, { renderImageUrl: lead.images.renders[0]?.url ?? null, rtl: (lang === 'auto' ? draft.api?.lang : lang) === 'ar' }),
                productIds: lead.products.slice(0, 4).map((p) => p.productId),
            });
            if (!result.success) {
                setNotice({ tone: 'error', text: result.message || t('LEAD_DESK.COMPOSER.SEND_FAILED') });
                return;
            }
            await markSent('email');
        } catch {
            setNotice({ tone: 'error', text: t('LEAD_DESK.COMPOSER.SEND_FAILED') });
        } finally {
            setSending(false);
        }
    };

    const lastContact = lead.desk.lastContactAt && lead.desk.lastContactChannel;

    return (
        <SectionCard
            title={t('LEAD_DESK.COMPOSER.TITLE')}
            description={lastContact ? (
                <span>
                    {t('LEAD_DESK.COMPOSER.LAST_CONTACT', { channel: t(`LEAD_DESK.COMPOSER.CHANNEL.${lead.desk.lastContactChannel!.toUpperCase()}`) })}
                    <RelativeTime value={lead.desk.lastContactAt} className="ms-1" />
                </span>
            ) : undefined}
            action={(
                <PillTabs<FollowUpChannel>
                    value={channel}
                    onChange={(c) => {
                        setChannel(c);
                        setNotice(null);
                    }}
                    ariaLabel={t('LEAD_DESK.COMPOSER.TITLE')}
                    options={[
                        { value: 'whatsapp', label: t('LEAD_DESK.COMPOSER.WHATSAPP') },
                        { value: 'email', label: t('LEAD_DESK.COMPOSER.EMAIL') },
                    ]}
                />
            )}
            testId="lead-composer"
        >
            <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-center gap-2">
                    <Button variant="outline" size="sm" onClick={write} disabled={writing || (channel === 'email' && unsubscribed)} data-testid="composer-write">
                        {writing ? <Loader2 className="animate-spin" /> : <Sparkles />}
                        {writing ? t('LEAD_DESK.COMPOSER.WRITING') : draft.text ? t('LEAD_DESK.COMPOSER.REWRITE') : t('LEAD_DESK.COMPOSER.WRITE')}
                    </Button>
                    <label className="ms-auto flex items-center gap-2 text-xs text-muted-foreground">
                        {t('LEAD_DESK.COMPOSER.LANGUAGE')}
                        <NativeSelect size="sm" value={lang} onChange={(e) => setLang(e.target.value as Lang)} aria-label={t('LEAD_DESK.COMPOSER.LANGUAGE')}>
                            {LANGS.map((l) => (
                                <NativeSelectOption key={l} value={l}>
                                    {l === 'auto' ? t('LEAD_DESK.COMPOSER.LANG_AUTO') : t(`LEAD_DESK.COMPOSER.LANG.${l.toUpperCase()}`)}
                                </NativeSelectOption>
                            ))}
                        </NativeSelect>
                    </label>
                </div>

                {channel === 'email' && (
                    <div className="flex flex-col gap-1.5">
                        <span className="text-xs text-muted-foreground">{t('LEAD_DESK.COMPOSER.TO', { to: lead.email })}</span>
                        <Input
                            value={draft.subject}
                            onChange={(e) => update({ subject: e.target.value })}
                            placeholder={t('LEAD_DESK.COMPOSER.SUBJECT')}
                            aria-label={t('LEAD_DESK.COMPOSER.SUBJECT')}
                            disabled={unsubscribed}
                        />
                    </div>
                )}

                <Textarea
                    value={draft.text}
                    onChange={(e) => update({ text: e.target.value })}
                    placeholder={t('LEAD_DESK.COMPOSER.PLACEHOLDER')}
                    rows={channel === 'whatsapp' ? 8 : 11}
                    dir={(lang === 'auto' ? draft.api?.lang : lang) === 'ar' ? 'rtl' : undefined}
                    className="text-sm leading-relaxed"
                    disabled={channel === 'email' && unsubscribed}
                    data-testid="composer-text"
                    aria-label={t('LEAD_DESK.COMPOSER.TITLE')}
                />

                {channel === 'whatsapp' && !lead.contact.phone && <p className="text-xs text-muted-foreground">{t('LEAD_DESK.COMPOSER.NO_PHONE')}</p>}
                {channel === 'whatsapp' && lead.contact.phone && draft.api && !draft.api.phoneResolved && (
                    <p className="text-xs text-muted-foreground">{t('LEAD_DESK.COMPOSER.PHONE_UNRESOLVED')}</p>
                )}
                {channel === 'email' && unsubscribed && <p className="text-xs text-destructive">{t('LEAD_DESK.COMPOSER.UNSUBSCRIBED')}</p>}

                <div className="flex flex-wrap items-center gap-2">
                    {channel === 'whatsapp' ? (
                        <>
                            <Button asChild size="sm" disabled={!draft.text.trim()}>
                                <a
                                    href={draft.text.trim() ? whatsappLinkFor(draft.api?.waLink, draft.text) : undefined}
                                    target="_blank"
                                    rel="noreferrer"
                                    aria-disabled={!draft.text.trim()}
                                    data-testid="composer-open-whatsapp"
                                >
                                    <MessageCircle />
                                    {t('LEAD_DESK.COMPOSER.OPEN_WHATSAPP')}
                                </a>
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => markSent('whatsapp').catch(() => setNotice({ tone: 'error', text: t('LEAD_DESK.COMPOSER.SEND_FAILED') }))} disabled={!draft.text.trim()} data-testid="composer-mark-sent">
                                <Check />
                                {t('LEAD_DESK.COMPOSER.MARK_SENT')}
                            </Button>
                        </>
                    ) : (
                        <Button size="sm" onClick={sendEmail} disabled={!draft.text.trim() || !draft.subject.trim() || sending || unsubscribed} data-testid="composer-send-email">
                            {sending ? <Loader2 className="animate-spin" /> : <Mail />}
                            {sending ? t('LEAD_DESK.COMPOSER.SENDING') : t('LEAD_DESK.COMPOSER.SEND_EMAIL')}
                        </Button>
                    )}
                    <span className="text-xs text-muted-foreground">
                        {channel === 'whatsapp' ? t('LEAD_DESK.COMPOSER.HINT_WHATSAPP') : t('LEAD_DESK.COMPOSER.HINT_EMAIL')}
                    </span>
                </div>

                {notice && (
                    <p className={notice.tone === 'ok' ? 'text-sm text-success' : 'text-sm text-destructive'} role={notice.tone === 'error' ? 'alert' : 'status'}>
                        {notice.text}
                    </p>
                )}
            </div>
        </SectionCard>
    );
}
